/**
 * Simple Bundles - Infinite Options Image Swap
 * Integrated with custom sb-io-swatches
 *
 * Uses:
 * - [data-sb-io]
 * - [data-sb-io-swatch]
 * - #sb-variant-options-map
 *
 * This version is intended to work with the existing sb-io-swatches snippet.
 */

(function () {
  'use strict';

  // ========================================================================
  // CONFIG
  // ========================================================================

  const CONFIG = {
    imageSelectors: [
      '.product__media-item--variant img',
      '.product__media img.image-magnify-full-size',
      '.product__media-list .product__media img',
      '.product__media img',
      '.product-image-main img',
      '.product__photos img',
      '.product__photo img.photoswipe__image',
      '.product__photo img',
      '.product-gallery__image img',
      '.product__main-photos img',
      '.product-main-slide .image-wrap img',
      '.product-featured-img',
      '.product-single__photo img',
      '.product-single__media img',
      '.product__media-item img',
      '[data-product-media-type="image"] img',
      '.product-single__image img',
      '.product__image-wrapper img',
    ],

    ioBundleContainer: '[data-sb-io]',
    transitionMs: 200,
  };

  // ========================================================================
  // STATE
  // ========================================================================

  let mainImageEl = null;
  let originalSrc = null;
  let originalSrcset = null;

  const imageMap = {};
  const productCache = {};

  // ========================================================================
  // HELPERS
  // ========================================================================

  function normalizeOptionText(text) {
    return String(text || '')
      .replace(/\([\+\-]?\s*[A-Z]{0,3}\$?\s*[\d,.]+\)/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function parsePageJson(id) {
    const el = document.getElementById(id);

    if (!el) {
      return {};
    }

    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      console.warn('[SB-IMG] Could not parse #' + id, e);
      return {};
    }
  }

  function getSelectedVariantId() {
    const url = new URL(window.location.href);
    const urlVariant = url.searchParams.get('variant');

    if (urlVariant) {
      return String(urlVariant);
    }

    const idField = document.querySelector(
      'form[action*="/cart/add"] [name="id"]'
    );

    if (idField && idField.value) {
      return String(idField.value);
    }

    return null;
  }

  function findMainImage() {
    for (const sel of CONFIG.imageSelectors) {
      const img = document.querySelector(sel);

      if (
        img &&
        img.src &&
        !img.closest(CONFIG.ioBundleContainer)
      ) {
        return img;
      }
    }

    const section =
      document.querySelector('.product') ||
      document.querySelector('[data-section-type="product"]') ||
      document.querySelector('.product-single') ||
      document.querySelector('main');

    if (!section) {
      return null;
    }

    let best = null;
    let bestArea = 0;

    section.querySelectorAll('img').forEach((img) => {
      if (img.closest(CONFIG.ioBundleContainer)) {
        return;
      }

      const area = img.offsetWidth * img.offsetHeight;

      if (area > bestArea) {
        best = img;
        bestArea = area;
      }
    });

    return best;
  }

  async function fetchProduct(handle) {
    if (productCache[handle]) {
      return productCache[handle];
    }

    try {
      const res = await fetch('/products/' + handle + '.js');

      if (!res.ok) {
        return null;
      }

      const data = await res.json();

      productCache[handle] = data;

      return data;
    } catch (e) {
      console.warn('[SB-IMG] Fetch failed for', handle, e);
      return null;
    }
  }

  function resizeImage(url, width) {
    if (!url) {
      return url;
    }

    return url
      .replace(/_(\d+x\d*|\d*x\d+)\./, '.')
      .replace(/\.(\w+)(\?.*)?$/, `_${width}x.$1$2`);
  }

  function swapImage(newSrc) {
    if (!newSrc) {
      return;
    }

    if (
      !mainImageEl ||
      !document.documentElement.contains(mainImageEl)
    ) {
      mainImageEl = findMainImage();

      if (mainImageEl) {
        originalSrc = mainImageEl.src;
        originalSrcset = mainImageEl.getAttribute('srcset');
      }
    }

    if (!mainImageEl) {
      console.warn('[SB-IMG] Main product image not found.');
      return;
    }

    const normCurrent = mainImageEl.src
      .replace(/[?#].*$/, '')
      .replace(/_\d+x\d*\./, '.');

    const normNew = newSrc
      .replace(/[?#].*$/, '')
      .replace(/_\d+x\d*\./, '.');

    if (normCurrent === normNew) {
      return;
    }

    mainImageEl.style.transition =
      `opacity ${CONFIG.transitionMs}ms ease`;

    mainImageEl.style.opacity = '0';

    setTimeout(() => {
      mainImageEl.removeAttribute('srcset');

      const picture = mainImageEl.closest('picture');

      if (picture) {
        picture
          .querySelectorAll('source')
          .forEach((source) => source.remove());
      }

      const zoomEl =
        mainImageEl.closest('[data-zoom]') ||
        mainImageEl.closest('[data-bgset]') ||
        mainImageEl.closest('[data-image]');

      if (zoomEl) {
        if (zoomEl.dataset.zoom) {
          zoomEl.dataset.zoom = resizeImage(newSrc, 2048);
        }

        if (zoomEl.dataset.bgset) {
          zoomEl.dataset.bgset = newSrc;
        }
      }

      mainImageEl.src = newSrc;

      const show = () => {
        mainImageEl.style.opacity = '1';
      };

      mainImageEl.addEventListener(
        'load',
        show,
        { once: true }
      );

      if (mainImageEl.complete) {
        show();
      }
    }, CONFIG.transitionMs);
  }

  function restoreOriginal() {
    if (!mainImageEl || !originalSrc) {
      return;
    }

    mainImageEl.style.transition =
      `opacity ${CONFIG.transitionMs}ms ease`;

    mainImageEl.style.opacity = '0';

    setTimeout(() => {
      mainImageEl.src = originalSrc;

      if (originalSrcset) {
        mainImageEl.setAttribute(
          'srcset',
          originalSrcset
        );
      }

      mainImageEl.style.opacity = '1';
    }, CONFIG.transitionMs);
  }

  // ========================================================================
  // BUNDLED VARIANT DATA
  // ========================================================================

  async function getBundledVariants() {
    const variantOptionsMap = parsePageJson(
      'sb-variant-options-map'
    );

    const variantId = getSelectedVariantId();

    if (!variantId) {
      console.warn(
        '[SB-IMG] Could not determine selected parent variant.'
      );

      return null;
    }

    const variantData = variantOptionsMap[variantId];

    if (
      variantData &&
      Array.isArray(variantData.bundled_variants)
    ) {
      return variantData.bundled_variants;
    }

    console.warn(
      '[SB-IMG] No bundled_variants found for parent variant:',
      variantId
    );

    return null;
  }

  // ========================================================================
  // IMAGE MAP
  // ========================================================================

  async function buildImageMap(bundledVariants) {
    if (
      !Array.isArray(bundledVariants) ||
      bundledVariants.length === 0
    ) {
      console.warn(
        '[SB-IMG] No bundled_variants data found.'
      );

      return;
    }

    const handleGroups = {};

    for (const bv of bundledVariants) {
      if (!bv.handle) {
        continue;
      }

      if (!handleGroups[bv.handle]) {
        handleGroups[bv.handle] = [];
      }

      handleGroups[bv.handle].push(bv);
    }

    const fetches = Object.keys(handleGroups).map(
      async (handle) => {
        const product = await fetchProduct(handle);

        if (!product) {
          console.warn(
            '[SB-IMG] Could not fetch product:',
            handle
          );

          return;
        }

        for (const bv of handleGroups[handle]) {
          let imageSrc = null;

          const matchedVariant =
            product.variants &&
            product.variants.find(
              (v) =>
                String(v.id) ===
                String(bv.variant_id)
            );

          if (
            matchedVariant &&
            matchedVariant.featured_image
          ) {
            if (
              typeof matchedVariant.featured_image ===
              'string'
            ) {
              imageSrc =
                matchedVariant.featured_image;
            } else if (
              matchedVariant.featured_image.src
            ) {
              imageSrc =
                matchedVariant.featured_image.src;
            }
          }

          if (!imageSrc && product.featured_image) {
            if (
              typeof product.featured_image ===
              'string'
            ) {
              imageSrc = product.featured_image;
            } else if (
              product.featured_image.src
            ) {
              imageSrc =
                product.featured_image.src;
            }
          }

          if (
            !imageSrc &&
            product.images &&
            product.images.length > 0
          ) {
            imageSrc = product.images[0];
          }

          if (!imageSrc) {
            console.log(
              '[SB-IMG] No image available for:',
              bv.product_title
            );

            continue;
          }

          imageSrc = resizeImage(
            imageSrc,
            1024
          );

          const keys = new Set();

          if (
            bv.variant_options &&
            Array.isArray(bv.variant_options)
          ) {
            for (const opt of bv.variant_options) {
              if (opt.value) {
                keys.add(
                  normalizeOptionText(opt.value)
                );
              }
            }
          }

          if (bv.product_title) {
            keys.add(
              normalizeOptionText(
                bv.product_title
              )
            );

            if (
              bv.variant_title &&
              bv.variant_title !==
                'Default Title'
            ) {
              keys.add(
                normalizeOptionText(
                  bv.product_title +
                    ' - ' +
                    bv.variant_title
                )
              );
            }
          }

          for (const key of keys) {
            imageMap[key] = imageSrc;
          }
        }
      }
    );

    await Promise.all(fetches);

    console.log(
      '[SB-IMG] Image map built:',
      Object.keys(imageMap).length,
      'entries',
      imageMap
    );
  }

  async function rebuildImageMapForCurrentVariant() {
    Object.keys(imageMap).forEach(
      (key) => delete imageMap[key]
    );

    const bundledVariants =
      await getBundledVariants();

    if (!bundledVariants) {
      return;
    }

    console.log(
      '[SB-IMG] bundled_variants loaded:',
      bundledVariants.length,
      'entries'
    );

    await buildImageMap(
      bundledVariants
    );
  }

  // ========================================================================
  // SWATCH EVENTS
  // ========================================================================

  function handleSwatch(swatch) {
    const raw =
      swatch.getAttribute(
        'data-option-value'
      ) ||
      swatch.textContent ||
      '';

    const normalized =
      normalizeOptionText(raw);

    console.log(
      '[SB-IMG] Swatch selected:',
      {
        raw: raw.trim(),
        normalized: normalized,
      }
    );

    const imageSrc =
      imageMap[normalized];

    if (imageSrc) {
      console.log(
        '[SB-IMG] Image found:',
        imageSrc
      );

      swapImage(imageSrc);

      return;
    }

    console.log(
      '[SB-IMG] No image for:',
      normalized
    );
  }

  function attachSwatchListener() {
    document.addEventListener(
      'click',
      (event) => {
        const swatch =
          event.target.closest(
            CONFIG.ioBundleContainer +
              ' [data-sb-io-swatch]'
          );

        if (!swatch) {
          return;
        }

        handleSwatch(swatch);
      }
    );

    console.log(
      '[SB-IMG] Swatch listener attached.'
    );
  }

  // ========================================================================
  // PARENT VARIANT CHANGES
  // ========================================================================

  function hookParentVariantChanges() {
    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;

        if (
          target &&
          (
            target.matches('[name="id"]') ||
            target.matches(
              'select[name="id"]'
            )
          )
        ) {
          setTimeout(
            rebuildImageMapForCurrentVariant,
            50
          );
        }
      }
    );

    window.addEventListener(
      'popstate',
      () => {
        setTimeout(
          rebuildImageMapForCurrentVariant,
          50
        );
      }
    );

    [
      'variant:change',
      'product:variant-change',
      'theme:variant:change',
    ].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          setTimeout(
            rebuildImageMapForCurrentVariant,
            50
          );
        }
      );
    });
  }

  // ========================================================================
  // INIT
  // ========================================================================

  async function init() {
    mainImageEl = findMainImage();

    if (!mainImageEl) {
      console.warn(
        '[SB-IMG] Main product image not found. Retrying in 1s...'
      );

      setTimeout(() => {
        mainImageEl = findMainImage();

        if (mainImageEl) {
          originalSrc =
            mainImageEl.src;

          originalSrcset =
            mainImageEl.getAttribute(
              'srcset'
            );

          console.log(
            '[SB-IMG] Main image found on retry.'
          );
        } else {
          console.error(
            '[SB-IMG] Could not find main product image.'
          );
        }
      }, 1000);
    } else {
      originalSrc =
        mainImageEl.src;

      originalSrcset =
        mainImageEl.getAttribute(
          'srcset'
        );

      console.log(
        '[SB-IMG] Main image found:',
        originalSrc
      );
    }

    await rebuildImageMapForCurrentVariant();

    attachSwatchListener();

    hookParentVariantChanges();

    console.log(
      '[SB-IMG] Initialized for sb-io-swatches.'
    );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }
})();